import { useEffect } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next/typescript/t";

import { useResetPasswordConfirmMutation } from "@/redux/service";
import { handleErrors } from "@/shared/utils/forms";
import { HookFormInput } from "@/components_v2/FormFields/ReactHookForm/fields";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import BaseButton from "@/components_v2/Button/BaseButton";
import { ResetPasswordConfirmFormValues } from "./types";

const resetPasswordConfirmSchema = (
  t: TFunction,
): yup.ObjectSchema<ResetPasswordConfirmFormValues> =>
  yup.object().shape({
    password: yup
      .string()
      .typeError(t("auth.validation.passwordNotValid"))
      .required(t("auth.validation.passwordRequired")),
    repeatPassword: yup
      .string()
      .typeError(t("auth.validation.repeatPasswordNotValid"))
      .required(t("auth.validation.passwordConfirm"))
      .oneOf([yup.ref("password")], t("auth.validation.passwordsMustMatch")),
  });

const ResetPasswordConfirm = () => {
  const navigate = useNavigate();
  const [resetPasswordConfirm, { isLoading }] =
    useResetPasswordConfirmMutation();
  const { notification } = App.useApp();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const form = useForm<ResetPasswordConfirmFormValues>({
    resolver: yupResolver(resetPasswordConfirmSchema(t)),
    mode: "onChange",
  });

  useEffect(() => {
    if (!searchParams.has("token")) {
      notification.success({
        message: t("auth.labels.invalidLink"),
        key: "invalid_reset_link",
      });
      navigate("/reset-password");
    }
  }, [notification, navigate, searchParams]);

  const onSubmit = (values: ResetPasswordConfirmFormValues) => {
    resetPasswordConfirm({
      ...values,
      token: searchParams.get("token") as string,
    })
      .unwrap()
      .then((data) => {
        notification.success({ message: data.message });
        navigate("/login");
      })
      .catch((errors) => handleErrors(errors, form.setError, notification));
  };

  return (
    <div className="flex flex-col gap-400">
      <h2 className="header-l-medium text-center">
        {t("auth.labels.resetPassword")}
      </h2>

      <div className="form-input-container">
        <FormFieldWrapper label={t("auth.labels.password")} required>
          <HookFormInput
            type="password"
            name="password"
            control={form.control}
          />
        </FormFieldWrapper>

        <FormFieldWrapper label={t("auth.labels.repeatPassword")} required>
          <HookFormInput
            type="password"
            name="repeatPassword"
            control={form.control}
          />
        </FormFieldWrapper>

        <div className="flex flex-col items-center">
          <BaseButton
            disabled={isLoading}
            isFullWidth
            onClick={form.handleSubmit(onSubmit)}
          >
            {t("auth.labels.submit")}
          </BaseButton>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordConfirm;
